<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useInventoryStore, type InventoryItem, type CreateItemRequest } from '../stores/inventory'
import PhotoUploader from '../components/PhotoUploader.vue'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const inventoryStore = useInventoryStore()

const item = ref<InventoryItem | null>(null)
const loading = ref(true)
const editMode = ref(false)
const showDeleteConfirm = ref(false)

const formData = ref<CreateItemRequest>({
  name: '',
  description: '',
  make: '',
  model: '',
  serial_number: '',
  purchase_location: '',
  purchase_date: '',
  purchase_price: undefined,
  warranty_expires: '',
  photo_key: '',
  location_id: '',
  category_id: '',
  condition: undefined,
  notes: '',
  manual_url: '',
})
const formError = ref('')
const saving = ref(false)

const API_BASE = import.meta.env.VITE_API_URL || ''

const isWarrantyExpired = computed(() => {
  if (!item.value?.warranty_expires) return false
  return new Date(item.value.warranty_expires) < new Date()
})

const currentPhotoUrl = computed(() => {
  if (formData.value.photo_key) {
    return `/api/inventory/photo/${formData.value.photo_key}`
  }
  return item.value?.photo_url || null
})

onMounted(async () => {
  if (!authStore.canViewInventory) {
    router.push('/')
    return
  }

  await inventoryStore.fetchCategories()

  const itemId = route.params.id as string
  const fetchedItem = await inventoryStore.fetchItem(itemId)

  if (!fetchedItem) {
    router.push('/inventory')
    return
  }

  item.value = fetchedItem
  loadFormFromItem()
  loading.value = false
})

function loadFormFromItem() {
  if (!item.value) return
  formData.value = {
    name: item.value.name,
    description: item.value.description || '',
    make: item.value.make || '',
    model: item.value.model || '',
    serial_number: item.value.serial_number || '',
    purchase_location: item.value.purchase_location || '',
    purchase_date: item.value.purchase_date || '',
    purchase_price: item.value.purchase_price || undefined,
    warranty_expires: item.value.warranty_expires || '',
    photo_key: item.value.photo_key || '',
    location_id: item.value.location_id || '',
    category_id: item.value.category_id || '',
    condition: item.value.condition || undefined,
    notes: item.value.notes || '',
    manual_url: item.value.manual_url || '',
  }
}

function startEdit() {
  loadFormFromItem()
  formError.value = ''
  editMode.value = true
}

function cancelEdit() {
  loadFormFromItem()
  editMode.value = false
}

async function saveItem() {
  if (!item.value || !formData.value.name) {
    formError.value = 'Name is required'
    return
  }

  saving.value = true
  formError.value = ''

  try {
    await inventoryStore.updateItem(item.value.id, formData.value)
    const updated = await inventoryStore.fetchItem(item.value.id)
    if (updated) {
      item.value = updated
    }
    editMode.value = false
  } catch (err) {
    formError.value = err instanceof Error ? err.message : 'Failed to save changes'
  } finally {
    saving.value = false
  }
}

async function deleteItem() {
  if (!item.value) return

  try {
    await inventoryStore.deleteItem(item.value.id)
    router.push('/inventory')
  } catch (err) {
    console.error('Failed to delete item:', err)
  }
}

function handlePhotoUploaded(key: string) {
  formData.value.photo_key = key
}

function handlePhotoRemoved() {
  formData.value.photo_key = ''
}

function handlePhotoError(message: string) {
  formError.value = message
}

function formatDate(date: string | null): string {
  if (!date) return '-'
  return new Date(date).toLocaleDateString()
}

function formatCurrency(amount: number | null): string {
  if (amount === null || amount === undefined) return '-'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
}

function getConditionLabel(condition: string | null): string {
  if (!condition) return 'Unknown'
  return condition.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
}

function getConditionColor(condition: string | null): string {
  switch (condition) {
    case 'excellent': return 'bg-green-100 text-green-800'
    case 'good': return 'bg-blue-100 text-blue-800'
    case 'fair': return 'bg-yellow-100 text-yellow-800'
    case 'poor': return 'bg-orange-100 text-orange-800'
    case 'needs_replacement': return 'bg-red-100 text-red-800'
    default: return 'bg-gray-100 text-gray-800'
  }
}
</script>

<template>
  <div class="max-w-4xl mx-auto p-4 md:p-6">
    <!-- Back Button -->
    <router-link to="/inventory" class="text-primary-600 hover:text-primary-700 mb-4 inline-flex items-center gap-1">
      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
      </svg>
      Back to Inventory
    </router-link>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-12">
      <p class="text-gray-500">Loading item...</p>
    </div>

    <!-- View Mode -->
    <div v-else-if="item && !editMode">
      <!-- Header -->
      <div class="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
        <div>
          <h1 class="text-2xl font-bold text-gray-800">{{ item.name }}</h1>
          <div class="flex flex-wrap gap-2 mt-2">
            <span
              v-if="item.location_name"
              class="px-2 py-1 bg-gray-100 text-gray-600 rounded text-sm"
            >
              {{ item.location_name }}
            </span>
            <span
              v-if="item.category_name"
              class="px-2 py-1 bg-gray-100 text-gray-600 rounded text-sm"
            >
              {{ item.category_name }}
            </span>
            <span
              v-if="item.condition"
              :class="['px-2 py-1 rounded text-sm', getConditionColor(item.condition)]"
            >
              {{ getConditionLabel(item.condition) }}
            </span>
          </div>
        </div>
        <div v-if="authStore.canManageInventory" class="flex gap-2">
          <button @click="startEdit" class="btn">Edit</button>
          <button @click="showDeleteConfirm = true" class="btn btn-danger">Delete</button>
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <!-- Photo -->
        <div class="card">
          <div class="h-64 bg-gray-100 rounded-lg overflow-hidden">
            <img
              v-if="item.photo_url"
              :src="`${API_BASE}${item.photo_url}`"
              :alt="item.name"
              class="w-full h-full object-contain"
            />
            <div v-else class="w-full h-full flex items-center justify-center text-gray-400">
              <svg class="w-24 h-24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
        </div>

        <!-- Details -->
        <div class="card space-y-4">
          <h2 class="font-semibold text-gray-700 border-b pb-2">Details</h2>

          <p v-if="item.description" class="text-gray-600">{{ item.description }}</p>

          <div class="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span class="text-gray-500">Make</span>
              <p class="text-gray-800">{{ item.make || '-' }}</p>
            </div>
            <div>
              <span class="text-gray-500">Model</span>
              <p class="text-gray-800">{{ item.model || '-' }}</p>
            </div>
            <div class="col-span-2">
              <span class="text-gray-500">Serial Number</span>
              <p class="text-gray-800 font-mono">{{ item.serial_number || '-' }}</p>
            </div>
          </div>

          <div v-if="item.manual_url" class="pt-2">
            <a
              :href="item.manual_url"
              target="_blank"
              rel="noopener"
              class="text-primary-600 hover:text-primary-700 text-sm inline-flex items-center gap-1"
            >
              View Manual
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        </div>

        <!-- Purchase Info -->
        <div class="card space-y-4">
          <h2 class="font-semibold text-gray-700 border-b pb-2">Purchase Information</h2>

          <div class="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span class="text-gray-500">Purchased From</span>
              <p class="text-gray-800">{{ item.purchase_location || '-' }}</p>
            </div>
            <div>
              <span class="text-gray-500">Purchase Date</span>
              <p class="text-gray-800">{{ formatDate(item.purchase_date) }}</p>
            </div>
            <div>
              <span class="text-gray-500">Price</span>
              <p class="text-gray-800">{{ formatCurrency(item.purchase_price) }}</p>
            </div>
            <div>
              <span class="text-gray-500">Warranty Expires</span>
              <p :class="isWarrantyExpired ? 'text-red-600' : 'text-gray-800'">
                {{ formatDate(item.warranty_expires) }}
                <span v-if="isWarrantyExpired" class="text-xs">(Expired)</span>
              </p>
            </div>
          </div>
        </div>

        <!-- Notes -->
        <div v-if="item.notes" class="card">
          <h2 class="font-semibold text-gray-700 border-b pb-2 mb-3">Notes</h2>
          <p class="text-gray-600 whitespace-pre-wrap">{{ item.notes }}</p>
        </div>
      </div>

      <!-- Meta Info -->
      <div class="mt-6 text-sm text-gray-400">
        <p>Added {{ formatDate(item.created_at) }} | Last updated {{ formatDate(item.updated_at) }}</p>
      </div>
    </div>

    <!-- Edit Mode -->
    <div v-else-if="item && editMode" class="card">
      <h2 class="text-xl font-bold text-gray-800 mb-4">Edit Item</h2>

      <div v-if="formError" class="bg-red-50 text-red-600 p-3 rounded-lg mb-4">
        {{ formError }}
      </div>

      <form @submit.prevent="saveItem" class="space-y-4">
        <!-- Photo -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Photo</label>
          <PhotoUploader
            :current-photo-url="currentPhotoUrl"
            upload-endpoint="inventory"
            @uploaded="handlePhotoUploaded"
            @removed="handlePhotoRemoved"
            @error="handlePhotoError"
          />
        </div>

        <!-- Basic Info -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input v-model="formData.name" type="text" class="input" required />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Condition</label>
            <select v-model="formData.condition" class="input">
              <option value="">Select condition</option>
              <option value="excellent">Excellent</option>
              <option value="good">Good</option>
              <option value="fair">Fair</option>
              <option value="poor">Poor</option>
              <option value="needs_replacement">Needs Replacement</option>
            </select>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <select v-model="formData.location_id" class="input">
              <option value="">Select location</option>
              <option
                v-for="loc in inventoryStore.locations"
                :key="loc.id"
                :value="loc.id"
              >
                {{ loc.name }}
              </option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select v-model="formData.category_id" class="input">
              <option value="">Select category</option>
              <option
                v-for="cat in inventoryStore.itemTypes"
                :key="cat.id"
                :value="cat.id"
              >
                {{ cat.name }}
              </option>
            </select>
          </div>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea v-model="formData.description" class="input" rows="2"></textarea>
        </div>

        <!-- Product Details -->
        <div class="border-t pt-4">
          <h3 class="font-medium text-gray-700 mb-3">Product Details</h3>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Make</label>
              <input v-model="formData.make" type="text" class="input" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Model</label>
              <input v-model="formData.model" type="text" class="input" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Serial Number</label>
              <input v-model="formData.serial_number" type="text" class="input" />
            </div>
          </div>
        </div>

        <!-- Purchase Info -->
        <div class="border-t pt-4">
          <h3 class="font-medium text-gray-700 mb-3">Purchase Information</h3>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Purchase Location</label>
              <input v-model="formData.purchase_location" type="text" class="input" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Purchase Date</label>
              <input v-model="formData.purchase_date" type="date" class="input" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Price</label>
              <input v-model.number="formData.purchase_price" type="number" step="0.01" class="input" />
            </div>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Warranty Expires</label>
              <input v-model="formData.warranty_expires" type="date" class="input" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Manual URL</label>
              <input v-model="formData.manual_url" type="url" class="input" placeholder="https://..." />
            </div>
          </div>
        </div>

        <!-- Notes -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Notes</label>
          <textarea v-model="formData.notes" class="input" rows="3"></textarea>
        </div>

        <!-- Actions -->
        <div class="flex gap-3 pt-4 border-t">
          <button type="button" @click="cancelEdit" class="btn flex-1">
            Cancel
          </button>
          <button
            type="submit"
            class="btn btn-primary flex-1"
            :disabled="saving"
          >
            {{ saving ? 'Saving...' : 'Save Changes' }}
          </button>
        </div>
      </form>
    </div>

    <!-- Delete Confirmation Modal -->
    <div
      v-if="showDeleteConfirm"
      class="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      @click.self="showDeleteConfirm = false"
    >
      <div class="card max-w-md w-full">
        <h2 class="text-xl font-bold text-gray-800 mb-4">Delete Item?</h2>
        <p class="text-gray-600 mb-6">
          Are you sure you want to delete "{{ item?.name }}"? This cannot be undone.
        </p>
        <div class="flex gap-3">
          <button @click="showDeleteConfirm = false" class="btn flex-1">
            Cancel
          </button>
          <button @click="deleteItem" class="btn btn-danger flex-1">
            Delete
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
